import type { FieldPath } from '../../types/Field';
import type { StudioAssetsActions } from '../../actions/studio/assets';
import { normalizeAssetUrlForProject } from '../../modules/project.utils';
import { fieldPathToString } from '../../modules/studio/utils-ts';
import { RESET_STUDIO_ACTION } from '../consts';

export interface StudioAsset {
    createdAt: string;
    fileName?: string;
    objectId: string;
    size?: number;
    url: string;
}

type StudioUploadBaseAsset<T extends UPLOAD_ASSET_TYPE, F, R> = {
    status: UPLOAD_ASSET_STATUS;
    type: T;
    fieldPath: FieldPath;
    fieldPathStr: string;
    startDate: Date;
    id: string;
    file: F;
    elapsedTime?: number;
    error?: Error;
    result?: R;
};

export type StudioUploadAssetRemoteStoreResult = {
    source: string;
    uri: string;
    metadata: {
        name: string;
        type: string;
    };
};

export type StudioUploadAssetRemoteStore = StudioUploadBaseAsset<UPLOAD_ASSET_TYPE.REMOTE_STORE, File, StudioUploadAssetRemoteStoreResult>;
export type StudioUploadAssetCMS = StudioUploadBaseAsset<UPLOAD_ASSET_TYPE.CMS, StudioUploadAssetRemoteStoreResult, StudioAsset>;

export enum UPLOAD_ASSET_STATUS {
    UPLOADING = 'UPLOADING',
    SUCCESS = 'SUCCESS',
    FAILED = 'FAILED'
}

export enum UPLOAD_ASSET_TYPE {
    REMOTE_STORE = 'REMOTE_STORE',
    CMS = 'CMS'
}

export type StudioUploadAsset = StudioUploadAssetRemoteStore | StudioUploadAssetCMS;

export interface State {
    data: StudioAsset[];
    isFetching: boolean;
    fetchError: Error | null;
    nextPageId: string | null;
    upload: StudioUploadAsset[];
}

const getDefaultState = (): State => ({
    data: [],
    isFetching: false,
    fetchError: null,
    nextPageId: null,
    upload: []
});

function addAssetsToArray(assets: StudioAsset[], newAssets: StudioAsset[], project: $TSFixMe): StudioAsset[] {
    const filteredAssets = newAssets.filter(({ objectId }) => !assets.find((existingAsset) => existingAsset.objectId === objectId));
    return assets.concat(
        filteredAssets.map((item) => {
            item.url = normalizeAssetUrlForProject(encodeURI(item.url), project);
            return item;
        })
    );
}

function retrieveAsset(assets: StudioUploadAsset[], id: string): StudioUploadAsset | null {
    const assetIndex = assets.findIndex((asset) => asset.id === id);
    if (assetIndex === -1) {
        return null;
    }
    const asset = assets.splice(assetIndex, 1)[0]!;
    return { ...asset };
}

const studioFrameReducer = (state = getDefaultState(), action: StudioAssetsActions | null): State => {
    switch (action?.type) {
        case 'FETCH_ASSETS_FAILED': {
            return {
                ...state,
                fetchError: action.data.error,
                isFetching: false
            };
        }

        case 'FETCH_ASSETS_STARTED': {
            const { resetAssets } = action.data || {};
            const res = {
                ...state,
                isFetching: true,
                fetchError: null
            };
            if (resetAssets) {
                res.data = [];
                res.nextPageId = null;
            }
            return res;
        }

        case 'FETCH_ASSETS_SUCCEEDED': {
            const { items, nextPageId, project, resetAssets } = action.data || {};
            return {
                ...state,
                data: addAssetsToArray(resetAssets ? [] : state.data, items, project),
                nextPageId: nextPageId,
                isFetching: false
            };
        }

        case 'ADD_ASSETS_TO_LIST': {
            return {
                ...state,
                data: addAssetsToArray(state.data, action.data.assets, action.data.project)
            };
        }

        case 'UPLOAD_ASSET_STARTED': {
            const upload = state.upload.slice();
            action.data.forEach((asset) => {
                const { id, fieldPath, file, type } = asset;
                const uploadingAsset = retrieveAsset(upload, id) || ({} as StudioUploadAsset);
                Object.assign(uploadingAsset, {
                    status: UPLOAD_ASSET_STATUS.UPLOADING,
                    startDate: new Date(),
                    fieldPath,
                    fieldPathStr: fieldPathToString(fieldPath),
                    file,
                    type,
                    id
                });
                delete uploadingAsset.result;
                upload.push(uploadingAsset);
            });
            return {
                ...state,
                upload
            };
        }

        case 'UPLOAD_ASSET_SUCCEEDED': {
            const upload = state.upload.slice();
            action.data.forEach((asset) => {
                const { id, result } = asset;
                const uploadingAsset = retrieveAsset(upload, id)!;
                uploadingAsset.status = UPLOAD_ASSET_STATUS.SUCCESS;
                uploadingAsset.result = result;
                uploadingAsset.elapsedTime = Date.now() - uploadingAsset.startDate.getTime();
                upload.push(uploadingAsset);
            });
            return {
                ...state,
                upload
            };
        }

        case 'UPLOAD_ASSET_FAILED': {
            const upload = state.upload.slice();
            const { error } = action;
            action.data.forEach((asset) => {
                const { id } = asset;
                const uploadingAsset = retrieveAsset(upload, id);
                if (uploadingAsset) {
                    uploadingAsset.status = UPLOAD_ASSET_STATUS.FAILED;
                    uploadingAsset.error = error;
                    uploadingAsset.elapsedTime = uploadingAsset.startDate ? Date.now() - uploadingAsset.startDate.getTime() : 0;
                    upload.push(uploadingAsset);
                }
            });
            return {
                ...state,
                upload
            };
        }

        case RESET_STUDIO_ACTION: {
            return getDefaultState();
        }

        default: {
            return state;
        }
    }
};

export default studioFrameReducer;

export const localStorePaths = [];
